'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';
import { LogOut, Clock, RefreshCw } from 'lucide-react';

const DEFAULT_TIMEOUT_MINUTES = 15;
const WARN_BEFORE_MS          =  2 * 60 * 1000; // warn 2 minutes before logout

// DOM events that count as user activity
const ACTIVITY_EVENTS = [
  'mousedown', 'mousemove', 'keydown',
  'touchstart', 'scroll', 'click', 'pointerdown',
] as const;

function WarningModal({
  secondsLeft,
  onStay,
  onLogout,
}: {
  secondsLeft: number;
  onStay: () => void;
  onLogout: () => void;
}) {
  const mins = Math.floor(secondsLeft / 60);
  const secs = secondsLeft % 60;
  const label = mins > 0
    ? `${mins}:${String(secs).padStart(2, '0')}`
    : `${secs}s`;

  const progress = secondsLeft / (WARN_BEFORE_MS / 1000);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden animate-[fadeInUp_0.25s_ease-out]">
        <div
          className="absolute top-0 left-0 h-1 bg-amber-400 transition-all duration-1000 ease-linear"
          style={{ width: `${progress * 100}%` }}
        />

        <div className="p-6 pt-7">
          <div className="flex items-center justify-center mb-4">
            <div className="w-14 h-14 rounded-2xl bg-amber-50 border border-amber-100 flex items-center justify-center">
              <Clock size={28} className="text-amber-500" />
            </div>
          </div>

          <h2 className="text-center text-lg font-bold text-gray-900 mb-1">
            Session expiring soon
          </h2>
          <p className="text-center text-sm text-gray-500 mb-5">
            You've been inactive. Your session will automatically end in{' '}
            <span className="font-semibold text-amber-600">{label}</span>.
          </p>

          <div className="flex justify-center mb-6">
            <div className="relative w-20 h-20">
              <svg className="w-full h-full -rotate-90" viewBox="0 0 80 80">
                <circle cx="40" cy="40" r="34" fill="none" stroke="#fef3c7" strokeWidth="6" />
                <circle
                  cx="40" cy="40" r="34" fill="none"
                  stroke="#f59e0b"
                  strokeWidth="6"
                  strokeDasharray={`${2 * Math.PI * 34}`}
                  strokeDashoffset={`${2 * Math.PI * 34 * (1 - progress)}`}
                  strokeLinecap="round"
                  className="transition-all duration-1000 ease-linear"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-base font-bold text-gray-800">{label}</span>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={onLogout}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
            >
              <LogOut size={14} />
              Sign out
            </button>
            <button
              onClick={onStay}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl transition-colors"
            >
              <RefreshCw size={14} />
              Stay logged in
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function IdleSessionProvider({ children }: { children: React.ReactNode }) {
  const { user, signOut } = useAuth();
  const router = useRouter();
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(WARN_BEFORE_MS / 1000);
  // Live timeout fetched from server; falls back to 15 minutes
  const timeoutMinutes = useRef<number>(DEFAULT_TIMEOUT_MINUTES);

  const idleTimer    = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warnTimer    = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Fetch the admin-configured timeout once on mount
  useEffect(() => {
    fetch('/api/admin/password-policy')
      .then(r => r.ok ? r.json() : null)
      .then(p => {
        if (p?.sessionTimeoutMinutes && p.sessionTimeoutMinutes > 0) {
          timeoutMinutes.current = p.sessionTimeoutMinutes;
        }
      })
      .catch(() => {});
  }, []);

  const clearAllTimers = () => {
    if (idleTimer.current)    clearTimeout(idleTimer.current);
    if (warnTimer.current)    clearTimeout(warnTimer.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
  };

  const handleForceLogout = useCallback(async () => {
    clearAllTimers();
    setShowWarning(false);
    try { await fetch('/api/auth/signout', { method: 'POST' }); } catch { /* ignore */ }
    router.replace('/session-expired');
  }, [router]);

  const resetTimer = useCallback(() => {
    if (!user) return;
    clearAllTimers();
    setShowWarning(false);

    const idleMs = timeoutMinutes.current * 60 * 1000;
    const warnAt = Math.max(0, idleMs - WARN_BEFORE_MS);

    warnTimer.current = setTimeout(() => {
      setShowWarning(true);
      setSecondsLeft(WARN_BEFORE_MS / 1000);

      countdownRef.current = setInterval(() => {
        setSecondsLeft(s => {
          if (s <= 1) {
            if (countdownRef.current) clearInterval(countdownRef.current);
            return 0;
          }
          return s - 1;
        });
      }, 1000);
    }, warnAt);

    idleTimer.current = setTimeout(() => {
      handleForceLogout();
    }, idleMs);
  }, [user, handleForceLogout]);

  useEffect(() => {
    if (!user) {
      clearAllTimers();
      setShowWarning(false);
      return;
    }

    const handler = () => resetTimer();

    ACTIVITY_EVENTS.forEach(ev =>
      document.addEventListener(ev, handler, { passive: true })
    );

    resetTimer();

    return () => {
      ACTIVITY_EVENTS.forEach(ev => document.removeEventListener(ev, handler));
      clearAllTimers();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleStayLoggedIn = () => {
    resetTimer();
  };

  return (
    <>
      {children}
      {showWarning && user && (
        <WarningModal
          secondsLeft={secondsLeft}
          onStay={handleStayLoggedIn}
          onLogout={handleForceLogout}
        />
      )}
    </>
  );
}
